import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

import { chargeKeys } from '@/hooks/use-charges';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { providerKeys } from '@/hooks/use-provider';
import api from '@/services/api';
import { actAsHeaders } from '@/store/actingProviderStore';
import {
  PaymentAccount,
  PaymentAccountActivationPayload,
  PaymentAccountStatusResponse,
  UpdateAcceptedMethodsPayload,
  UpdatePaymentCollectionPayload,
} from '@/types/payment-account';

export const paymentAccountKeys = {
  all: ['payment-account'] as const,
  self: () => [...paymentAccountKeys.all, 'me'] as const,
  of: (providerId: string) => [...paymentAccountKeys.all, providerId] as const,
};

/**
 * `GET /payment-account` (T1.4): devolve `{ account, prefill }`
 * (`PaymentAccountStatusResponse`) — `account` é `null` até o prestador
 * concluir o assistente (o envio é único, seção 2.5), `prefill` alimenta as
 * sugestões das etapas 1 e 4. A tela de "Como funciona" e o card do Perfil
 * tratam `account: null` como "ainda não ativou", não como erro.
 */
export function usePaymentAccount(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentAccountKeys.self(),
    queryFn: async () => (await api.get<PaymentAccountStatusResponse>('/payment-account')).data,
    enabled: isHydrated && options?.enabled !== false,
  });
}

/**
 * Estado da subconta de um profissional da equipe, sem trocar o foco — mesmo
 * padrão de `useServicesOf` (`use-services.ts`, T7.2). Quem chama não
 * gerencia (`canManage` recusa fora do caixa único/dono — a API mascara e
 * devolve só status/pendências/`paymentCollection`, nunca documento ou
 * banco), então isto é só para MOSTRAR se o profissional ativou (modo "por
 * profissional"), no detalhe dele em `team/[id].tsx`.
 */
export function usePaymentAccountOf(providerId: string | null | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentAccountKeys.of(providerId ?? ''),
    queryFn: async () =>
      (
        await api.get<PaymentAccountStatusResponse>('/payment-account', {
          ...actAsHeaders(providerId),
        })
      ).data,
    enabled: isHydrated && !!providerId,
  });
}

/**
 * `POST /payment-account` (T1.4): último passo do assistente de ativação
 * (seção 6.3, etapa 5 → 6). Manda os dados **uma única vez**; o servidor cria
 * a subconta no Stripe e devolve o estado já mascarado (`PaymentAccount`,
 * não o envelope `{ account, prefill }` do GET). Depois disso o app abre o
 * componente embutido do Stripe (T4.4) para a confirmação final. É
 * idempotente do lado do servidor: reenviar com uma conta já criada devolve
 * a mesma conta em vez de duplicar — por isso a tela de ativação pode chamar
 * de novo sem medo se a resposta anterior se perder.
 */
export function useActivatePaymentAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PaymentAccountActivationPayload) =>
      (await api.post<PaymentAccount>('/payment-account', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentAccountKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}

/**
 * `PATCH /payment-account/collection` (T2.4, contrato assumido): liga ou
 * desliga o recebimento automático. Ligar exige a conta `ACTIVE`; desligar
 * volta as cobranças automáticas em aberto para manual — por isso invalida
 * também as cobranças, não só a conta.
 */
export function useUpdatePaymentCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdatePaymentCollectionPayload) =>
      (await api.patch<PaymentAccount>('/payment-account/collection', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentAccountKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: chargeKeys.all });
    },
  });
}

/**
 * `PATCH /payment-account/accepted-methods` (T6.4, PLANO 6.5): escolhe quais
 * formas oferecer no checkout automático. Sem tela ainda — a tela de
 * configurar recebimento é a T6.4; este hook só liga o app ao endpoint que a
 * API já expõe (`0380a3a`).
 */
export function useUpdateAcceptedMethods() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateAcceptedMethodsPayload) =>
      (await api.patch<PaymentAccount>('/payment-account/accepted-methods', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentAccountKeys.all });
    },
  });
}

/**
 * `POST /payment-account/onboarding-link`: cadastro hospedado pelo Stripe.
 * Web troca a página inteira (o Stripe devolve em `/payments/activate`);
 * nativo abre o navegador do sistema e, ao fechar, recarrega o status.
 */
export function useOpenStripeOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { url } = (
        await api.post<{ url: string }>('/payment-account/onboarding-link')
      ).data;
      if (Platform.OS === 'web') {
        window.location.assign(url);
        return;
      }
      await WebBrowser.openBrowserAsync(url);
      queryClient.invalidateQueries({ queryKey: paymentAccountKeys.all });
    },
  });
}

/**
 * `POST /payment-account/sync`: na volta do cadastro do Stripe, puxa o estado
 * da conta na hora em vez de esperar o webhook. Grava a resposta no cache.
 */
export function useSyncPaymentAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      (await api.post<PaymentAccountStatusResponse>('/payment-account/sync')).data,
    onSuccess: (data) => {
      queryClient.setQueryData(paymentAccountKeys.self(), data);
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}
